import * as THREE from 'three'

/**
 * 规整 GLB 节点名称。
 *
 * @param input 原始输入。
 * @returns 可写入 GLB 的短名称。
 */
export function normalizeDisplayNodeName(input: string): string {
  const normalized = input
    .trim()
    .replace(/[^\w.-]+/g, '_')
    .replace(/_+/g, '_')
  return normalized.length > 0 ? normalized : 'mesh'
}

/**
 * 读取 `.puppy` display mesh 已持久化的稳定节点 ID。
 *
 * @param source 源 mesh。
 * @returns displayNodeId；普通 mesh 返回空字符串。
 */
export function readPersistedDisplayNodeId(source: THREE.Mesh): string {
  const puppy = source.userData?.puppy as { displayNodeId?: unknown } | undefined
  return typeof puppy?.displayNodeId === 'string' ? puppy.displayNodeId.trim() : ''
}

/**
 * 复制 Object3D 局部变换与渲染标记。
 *
 * @param source 源对象。
 * @param target 目标对象。
 */
function copyObjectSnapshotState(source: THREE.Object3D, target: THREE.Object3D): void {
  target.position.copy(source.position)
  target.quaternion.copy(source.quaternion)
  target.scale.copy(source.scale)
  target.matrix.copy(source.matrix)
  target.matrixAutoUpdate = source.matrixAutoUpdate
  target.visible = source.visible
  target.castShadow = source.castShadow
  target.receiveShadow = source.receiveShadow
  target.frustumCulled = source.frustumCulled
}

/**
 * 创建 GLB 导出的层级 Group 快照。
 *
 * @param input 创建输入。
 * @returns 临时 Group。
 */
export function createDisplaySnapshotGroup(input: {
  /** 源对象。*/
  source: THREE.Object3D
  /** 装配节点 ID。*/
  assemblyNodeId: string
}): THREE.Group {
  const group = new THREE.Group()
  copyObjectSnapshotState(input.source, group)
  group.name = normalizeDisplayNodeName(input.source.name || input.assemblyNodeId)
  group.userData = {
    puppy: {
      assemblyNodeId: input.assemblyNodeId,
      assemblyNodeRole: 'node',
    },
  }
  return group
}

/**
 * 创建 GLB 导出的 mesh 快照。
 *
 * @param input 创建输入。
 * @returns 临时 mesh。
 */
export function createDisplaySnapshotMesh(input: {
  /** 源 mesh。*/
  source: THREE.Mesh
  /** display node ID。*/
  displayNodeId: string
  /** 装配节点 ID。*/
  assemblyNodeId: string
  /** 当前 mesh 是否拥有独立装配节点。*/
  ownsAssemblyNode: boolean
}): THREE.Mesh {
  const mesh = input.source instanceof THREE.InstancedMesh
    ? createDisplaySnapshotInstancedMesh(input.source)
    : new THREE.Mesh(input.source.geometry, input.source.material)
  copyObjectSnapshotState(input.source, mesh)
  /**
   * display.glb 节点名只承担稳定绑定职责，业务展示名由 assembly.json 负责。
   * GLTFLoader 可能把父节点名拼入 primitive mesh 的运行时名称；若继续把 source.name 写回，
   * 每次“导入后再保存”都会新增一层前缀。使用稳定 displayNodeId 可从协议边界阻断该累积。
   */
  mesh.name = normalizeDisplayNodeName(input.displayNodeId)
  mesh.userData = {
    puppy: {
      displayNodeId: input.displayNodeId,
      assemblyNodeId: input.assemblyNodeId,
      assemblyNodeRole: input.ownsAssemblyNode ? 'node' : 'display',
    },
  }
  return mesh
}

/**
 * 创建保留全部实例矩阵的 InstancedMesh 显示快照。
 *
 * Geometry 与 Material 仍由正式资源作用域持有；快照拥有独立的 instanceMatrix 与可选 instanceColor，供
 * GLTFExporter 写入 `EXT_mesh_gpu_instancing`，禁止退化成普通 Mesh 丢失实例槽。
 *
 * @param source 正式实例化显示对象。
 * @returns 与 source 实例数量和矩阵一致的临时 InstancedMesh。
 */
function createDisplaySnapshotInstancedMesh(source: THREE.InstancedMesh): THREE.InstancedMesh {
  const snapshot = new THREE.InstancedMesh(source.geometry, source.material, source.count)
  snapshot.instanceMatrix.copy(source.instanceMatrix)
  snapshot.instanceMatrix.needsUpdate = true
  if (source.instanceColor) {
    // 当前 clone() 声明返回普通 BufferAttribute；显式构造子类后 copy，保留实例类型及 meshPerAttribute。
    // copy() 会重新分配颜色数组，快照不会与正式实例共享可变缓冲区。
    snapshot.instanceColor = new THREE.InstancedBufferAttribute(
      source.instanceColor.array,
      source.instanceColor.itemSize,
    ).copy(source.instanceColor)
    snapshot.instanceColor.needsUpdate = true
  }
  return snapshot
}
